import React, { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { type CSSProperties } from '../../style/scrollbars';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Separator } from '../ui/separator';

type SettingProps = {
  title?: ReactNode;
  primaryAction?: ReactNode;
  style?: CSSProperties;
  children: ReactNode;
};

export const Setting = ({
  title,
  primaryAction,
  children,
}: SettingProps) => {
  return (
    <Card className="w-full gap-0">
      <CardHeader className="pb-4">
        {title && <CardTitle className="text-base">{title}</CardTitle>}
        <CardDescription className="text-sm leading-relaxed [&_strong]:text-foreground [&_strong]:font-medium">
          {children}
        </CardDescription>
      </CardHeader>
      {primaryAction && (
        <>
          <Separator />
          <CardContent className="pt-4">{primaryAction}</CardContent>
        </>
      )}
    </Card>
  );
};

type AdvancedToggleProps = {
  children: ReactNode;
};

export const AdvancedToggle = ({ children }: AdvancedToggleProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(location.hash === '#advanced');

  return (
    <Collapsible
      id="advanced"
      open={open}
      onOpenChange={setOpen}
      className="w-full"
    >
      {!open && (
        <CollapsibleTrigger asChild>
          <button
            data-testid="advanced-settings"
            className="text-sm text-primary hover:underline underline-offset-4 mb-6 block"
          >
            {t('Show advanced settings')}
          </button>
        </CollapsibleTrigger>
      )}
      <CollapsibleContent className="space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <h3 className="text-base font-semibold">
            {t('Advanced Settings')}
          </h3>
          <Separator className="flex-1" />
          <CollapsibleTrigger asChild>
            <button className="text-xs text-muted-foreground hover:text-foreground">
              {t('Hide')}
            </button>
          </CollapsibleTrigger>
        </div>
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};
